import PassportResultSummary from './PassportResultSummary';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import PassportCard from './PassportCard';
import { useAuth } from '../../contexts/AuthContext';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import PageLoader from '../ui/PageLoader';
import {
  previewPassportScan,
  submitPassportScan,
  type PassportScanResult,
  type PassportSubjectPreview,
} from '../../services/api/passport';
import {
  passportScanDuplicateMessage,
  passportScanErrorFallback,
  passportScanNotCheckInNote,
  passportScanOwnBuildMessage,
  passportScanPendingMessage,
  passportScanSignInPrompt,
  passportScanSuccessMessage,
} from './passportCopy';

type Phase = 'idle' | 'submitting' | 'success' | 'duplicate' | 'own_build' | 'error';

const PassportScanLandingPage: React.FC = () => {
  const { token: tokenParam } = useParams<{ token: string }>();
  const token = tokenParam ? decodeURIComponent(tokenParam) : '';
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<PassportScanResult | null>(null);
  const [preview, setPreview] = useState<PassportSubjectPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [needsLocation, setNeedsLocation] = useState(false);
  const [locationFix, setLocationFix] = useState<{
    lat: number;
    lng: number;
    accuracy_m: number;
    timestamp: number;
  }>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const submittedRef = useRef<string | null>(null);

  const payload = token ? `${window.location.origin}/passport/s/${encodeURIComponent(token)}` : '';

  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await previewPassportScan(payload);
        if (cancelled) return;
        setNeedsLocation(Boolean(data?.requires_location));
        setPreviewReady(true);
        if (data?.subject) setPreview(data.subject);
        else setPreviewFailed(true);
      } catch {
        if (!cancelled) {
          setPreviewFailed(true);
          setPreviewReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  useEffect(() => {
    if (
      authLoading ||
      !isAuthenticated ||
      !payload ||
      !previewReady ||
      (needsLocation && !locationFix)
    )
      return;
    const submitKey = `${payload}:${retryKey}`;
    if (submittedRef.current === submitKey) return;
    submittedRef.current = submitKey;

    let cancelled = false;
    (async () => {
      setPhase('submitting');
      setErrorMessage(null);
      void apiService.trackEvent('passport_scan_started', {
        metadata: { source_platform: 'web', entry: 'scan_landing' },
      });
      try {
        const scanResult = await submitPassportScan({
          payload,
          method: 'qr_scan',
          location: locationFix,
          client_scan_id: `web-landing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
        if (cancelled) return;
        setResult(scanResult);
        if (scanResult.status === 'revoked' || scanResult.status === 'rejected') {
          setErrorMessage('This record is no longer eligible. Open Passport to review its current status.');
          setPhase('error');
          return;
        }
        if (scanResult.owner_self) {
          setPhase('own_build');
          return;
        }
        if (
          scanResult.dedupe_outcome === 'already_recorded' &&
          !scanResult.cards?.some((c) => c.is_new)
        ) {
          setPhase('duplicate');
          void apiService.trackEvent('passport_encounter_duplicate', {
            metadata: { subject_type: scanResult.subject?.subject_type },
          });
          return;
        }
        setPhase('success');
        void apiService.trackEvent(
          scanResult.status === 'pending'
            ? 'passport_encounter_pending'
            : 'passport_scan_completed',
          { metadata: { verification_level: scanResult.verification_level } }
        );
      } catch (err: unknown) {
        if (cancelled) return;
        const code = (err as { code?: string } | null)?.code;
        if (code === 'dedupe_conflict') {
          setPhase('duplicate');
          void apiService.trackEvent('passport_encounter_duplicate');
          return;
        }
        setPhase('error');
        setErrorMessage(err instanceof Error ? err.message : passportScanErrorFallback);
        void apiService.trackEvent('passport_scan_failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, payload, retryKey, previewReady, needsLocation, locationFix]);

  const returnPath = token ? `/passport/s/${encodeURIComponent(token)}` : '/passport';
  const subjectTitle = preview?.title || result?.subject?.title;

  if (authLoading) {
    return <PageLoader />;
  }

  if (!token) {
    return (
      <PageShell width="narrow" contentClassName="!py-8">
        <PageHeader
          eyebrow="Bilt:Spec Passport"
          title="Missing scan code"
          description="This link does not include a Passport scan token."
        />
        <p className="text-muted text-sm mt-4">{passportScanNotCheckInNote}</p>
      </PageShell>
    );
  }

  return (
    <PageShell width="narrow" contentClassName="!py-8">
      <div className="card p-6 sm:p-8 space-y-5">
        <p className="text-xs uppercase tracking-[0.22em] text-meta">Bilt:Spec Passport</p>
        {preview?.thumbnail_url ? (
          <img
            src={preview.thumbnail_url}
            alt=""
            className="h-20 w-20 rounded-lg object-cover border border-white/10"
          />
        ) : null}
        {subjectTitle ? (
          <div>
            <h1 className="font-display text-2xl text-white">{subjectTitle}</h1>
            {preview?.subtitle ? (
              <p className="text-muted text-sm mt-1">{preview.subtitle}</p>
            ) : null}
          </div>
        ) : (
          <h1 className="font-display text-2xl text-white">Found something worth keeping.</h1>
        )}
        {isAuthenticated && needsLocation && !locationFix && (
          <div className="mt-6">
            <p className="text-zinc-300">
              This card is collected at the venue. Allow a one-time location check to continue.
            </p>
            <button
              className="pc-button mt-4"
              onClick={() => {
                if (!navigator.geolocation) {
                  setErrorMessage(
                    'Location is unavailable in this browser. Ask the host for help.'
                  );
                  return;
                }
                navigator.geolocation.getCurrentPosition(
                  (p) =>
                    setLocationFix({
                      lat: p.coords.latitude,
                      lng: p.coords.longitude,
                      accuracy_m: p.coords.accuracy,
                      timestamp: p.timestamp,
                    }),
                  () =>
                    setErrorMessage(
                      'Location could not be confirmed. Enable location or ask the host for help.'
                    ),
                  { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
                );
              }}
            >
              Check my location and collect
            </button>
          </div>
        )}
        <PassportResultSummary result={result?.ecosystem} />
        {result?.card_outcomes?.map((outcome, i) => (
          <p className="text-amber-200 text-sm my-3" key={i}>
            {outcome.title}: {outcome.message}
          </p>
        ))}
        {result?.cards
          ?.filter((c) => c.is_new)
          .map((c) => (
            <div className="max-w-[280px] mx-auto my-8" key={c.key}>
              <PassportCard card={c} />
            </div>
          ))}
        <p className="text-muted text-sm">{passportScanNotCheckInNote}</p>

        {!isAuthenticated ? (
          <>
            <p className="text-sm text-white">{passportScanSignInPrompt}</p>
            {previewFailed ? (
              <p className="text-muted text-sm">
                We could not preview this code. Sign in to try logging it anyway.
              </p>
            ) : null}
            <Link to="/login" state={{ from: returnPath }} className="btn-primary inline-flex">
              Sign in
            </Link>
          </>
        ) : null}

        {isAuthenticated && phase === 'submitting' ? (
          <p className="text-muted text-sm" aria-busy="true">
            Checking this QR…
          </p>
        ) : null}

        {phase === 'own_build' ? (
          <>
            <p className="text-sm text-white">{passportScanOwnBuildMessage}</p>
            <Link to="/passport" className="btn-secondary inline-flex">
              Open Passport
            </Link>
          </>
        ) : null}

        {phase === 'duplicate' ? (
          <>
            <p className="text-sm text-white">{passportScanDuplicateMessage}</p>
            <div className="flex flex-wrap gap-2">
              {result?.public_id ? (
                <Link
                  to={`/passport/encounters/${encodeURIComponent(result.public_id)}`}
                  className="btn-primary inline-flex"
                >
                  View encounter
                </Link>
              ) : null}
              <Link to="/passport" className="btn-secondary inline-flex">
                Open Passport
              </Link>
            </div>
          </>
        ) : null}

        {errorMessage && phase !== 'error' && (
          <p role="alert" className="pc-error">
            {errorMessage}
          </p>
        )}
        {phase === 'error' ? (
          <>
            <div className="alert-error text-sm" role="alert">
              {errorMessage || passportScanErrorFallback}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  if (needsLocation) setLocationFix(undefined);
                  setPhase('idle');
                  setRetryKey((k) => k + 1);
                }}
              >
                Try again
              </button>
              <Link to="/passport" className="btn-secondary inline-flex">
                Open Passport
              </Link>
            </div>
          </>
        ) : null}

        {phase === 'success' ? (
          <>
            <p className="text-sm text-white">
              {result?.status === 'pending'
                ? passportScanPendingMessage
                : passportScanSuccessMessage(
                    subjectTitle || 'that build',
                    result?.verification_level
                  )}
            </p>
            <div className="flex flex-wrap gap-2">
              {result?.public_id ? (
                <Link
                  to={`/passport/encounters/${encodeURIComponent(result.public_id)}`}
                  className="btn-primary inline-flex"
                >
                  View encounter
                </Link>
              ) : null}
              <Link to="/passport" className="btn-secondary inline-flex">
                Open Passport
              </Link>
            </div>
          </>
        ) : null}
      </div>
    </PageShell>
  );
};

export default PassportScanLandingPage;
